import React, { useMemo, useState } from 'react';
import { MergeVarDoc, useMergeVars } from '../../hooks/useComms';

type props = {
  handleToggleModel: () => void;
};

const TemplateModel: React.FunctionComponent<props> = (props): JSX.Element => {
  const { data, isLoading, error } = useMergeVars(true);
  const [showMandrillVars, setShowMandrillVars] = useState(false);

  const visibleVariables = useMemo(() => {
    if (!data) return [];
    return data.variables.filter((variable) => showMandrillVars || variable.editor_visible);
  }, [data, showMandrillVars]);

  const grouped = useMemo(() => {
    if (!data) return [];
    return data.categories
      .map((category) => ({
        ...category,
        variables: visibleVariables.filter((variable) => variable.category === category.id),
      }))
      .filter((category) => category.variables.length > 0);
  }, [data, visibleVariables]);

  const hiddenCount = useMemo(() => {
    if (!data) return 0;
    return data.variables.filter((variable) => !variable.editor_visible).length;
  }, [data]);

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden outline-none focus:outline-none">
        <div className="relative mx-auto my-6 w-full max-w-4xl px-4">
          <div className="relative flex max-h-[85vh] w-full flex-col overflow-hidden rounded-lg border-0 bg-white shadow-lg outline-none focus:outline-none">
            <div className="border-b border-gray-200 px-8 pb-4 pt-8">
              <div className="blue-primary text-2xl">Replacement Variables</div>
              <p className="text-gray-dark mt-2 text-sm leading-relaxed">
                Tokens for Switchboard subject, message body, and SMS copy. Mandrill HTML chrome (logos, accent color,
                Powered By flags) is hidden by default — those belong in the email template, not here.
              </p>
              {hiddenCount > 0 && (
                <label className="text-gray-dark mt-3 flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300"
                    checked={showMandrillVars}
                    onChange={(event) => setShowMandrillVars(event.target.checked)}
                  />
                  <span>
                    Show Mandrill / template variables
                    <span className="text-gray-dark ml-1 opacity-80">({hiddenCount})</span>
                  </span>
                </label>
              )}
            </div>

            <div className="flex-1 overflow-y-auto px-8 py-5">
              {isLoading && <div className="text-gray-dark py-6 text-sm">Loading replacement variables…</div>}
              {error && (
                <div className="py-6 text-sm text-red-600">
                  Could not load replacement variables. Try again or check that you can manage communications.
                </div>
              )}
              {!isLoading && !error && grouped.length > 0 && (
                <div className="space-y-6">
                  {grouped.map((category) => (
                    <section key={category.id}>
                      <h3 className="text-gray-dark mb-2 text-xs font-semibold uppercase tracking-wide">
                        {category.label}
                      </h3>
                      <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
                        {category.variables.map((variable) => (
                          <VariableRow key={variable.key} variable={variable} />
                        ))}
                      </ul>
                    </section>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-center border-t border-gray-200 py-4">
              <button
                className="bg-gray-light rounded-full px-7 py-2 text-sm text-white"
                type="button"
                onClick={props.handleToggleModel}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="fixed inset-0 z-40 bg-black opacity-25" onClick={props.handleToggleModel} />
    </>
  );
};

const VariableRow: React.FunctionComponent<{ variable: MergeVarDoc }> = ({ variable }) => {
  return (
    <li className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-start sm:gap-6">
      <code className="text-blue-primary shrink-0 font-mono text-sm font-medium sm:w-56">{`{{${variable.key}}}`}</code>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-gray-800">{variable.description}</p>
        {variable.caveat && (
          <p className="text-gray-dark mt-1 text-xs leading-relaxed">
            <span className="font-semibold">Note: </span>
            {variable.caveat}
          </p>
        )}
        {!variable.editor_visible && (
          <p className="mt-1 text-xs font-medium text-amber-800">Mandrill / email template only</p>
        )}
      </div>
    </li>
  );
};

export default TemplateModel;
